import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useRejectionReasons } from '@/hooks/use-recruitment';
import type { PipelineCard } from '@/types/recruitment';
import { useEffect, useState } from 'react';

interface RejectReasonDialogProps {
  open: boolean;
  card: PipelineCard | null;
  /** Called with `false` when the user cancels or closes — the caller reverts the drag. */
  onOpenChange: (open: boolean) => void;
  onConfirm: (reasonId: string, comment: string) => void;
  isPending: boolean;
}

const MAX_COMMENT_LENGTH = 500;

export function RejectReasonDialog({
  open,
  card,
  onOpenChange,
  onConfirm,
  isPending,
}: RejectReasonDialogProps) {
  const [reasonId, setReasonId] = useState('');
  const [comment, setComment] = useState('');
  const { data: reasons, isLoading } = useRejectionReasons();
  const hasReasons = (reasons?.length ?? 0) > 0;

  useEffect(() => {
    if (!open) {
      setReasonId('');
      setComment('');
    }
  }, [open]);

  if (!card) return null;

  const { candidate } = card;
  const fullName = `${candidate.firstName} ${candidate.lastName}`.trim();
  const canSubmit = reasonId.length > 0 && !isPending;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(reasonId, comment.trim());
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !isPending && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select Rejection Reason</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 p-3">
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={candidate.photoUrl ?? undefined} />
              <AvatarFallback className="bg-muted text-xs font-semibold">
                {candidate.initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground" title={fullName}>
                {fullName}
              </p>
              <p className="truncate text-xs text-muted-foreground">Marking as rejected</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Select value={reasonId} onValueChange={setReasonId} disabled={isLoading || !hasReasons}>
              <SelectTrigger id="reject-reason">
                <SelectValue
                  placeholder={
                    isLoading
                      ? 'Loading reasons…'
                      : !hasReasons
                        ? 'No reasons configured — add them in recruitment settings'
                        : 'Select a reason'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(reasons ?? []).map((reason) => (
                  <SelectItem
                    key={reason.id}
                    value={reason.id}
                    className="[&>span:last-child]:truncate"
                    title={reason.label}
                  >
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isLoading && !hasReasons && (
              <p className="text-[11px] text-muted-foreground">
                No rejection reasons configured. Add them in recruitment settings.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reject-comment">Comment (optional)</Label>
            <Textarea
              id="reject-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
              placeholder="Add any additional context…"
              rows={3}
            />
            <div className="flex justify-end text-[11px] text-muted-foreground tabular-nums">
              {comment.length} / {MAX_COMMENT_LENGTH}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={!canSubmit}>
            {isPending ? 'Rejecting…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
